import dayjs from 'dayjs';
import { message } from 'antd';
import { RefreshStatus } from '../../../App';
import { AssetCategory } from '../../../Utils/accountUtils';
import { analyticsStatusDataRefType } from '../../../Utils/types';
import { doesFiTypeExist } from './assetUtils';
import {
  convertToMonthYear,
  formatToINR,
  getMonthAbbreviation,
} from '../../../Utils/utils';
import {
  AccountCashflowCategoryType,
  CREDIT_EXCLUDED_CATEGORIES,
  DEBIT_EXCLUDED_CATEGORIES,
  FAILED_STATUSES,
} from './constants';

export type categoryTrendsType = 'CREDIT' | 'INVESTMENT' | 'DEBIT';
export type categoryActionType = 'Incoming' | 'Expenses' | 'Investment';

type handleExistingTrendsDataType = {
  trends: any;
  trendKey: string;
  graphSelectedMonth: string;
  categoryType: categoryTrendsType;
};

export const categoryTermMapping: Record<
  categoryTrendsType,
  categoryActionType
> = {
  CREDIT: 'Incoming',
  DEBIT: 'Expenses',
  INVESTMENT: 'Investment',
};

export const getTotalSavingsAmount = (
  statusData: any,
  datafromAnalyticsStatus: React.RefObject<analyticsStatusDataRefType | null>
): number | null => {  
  if (doesFiTypeExist(AssetCategory.BANKS, statusData)) {
    return Math.round(datafromAnalyticsStatus.current?.savings ?? 0);
  }
  return null;
};

export const filteredLoadingAndFailedAccounts = (account: any[]) => {
  const failed: any[] = [];
  const loading: any[] = [];
  account.forEach((account) => {
    if (FAILED_STATUSES.includes(account.lastRequestFIStatus)) {
      failed.push({ ...account, dataFailed: true });
    } else {
      loading.push({ ...account, pending: true });
    }
  });
  return { failed, loading };
};
export const confirmRevoke = (dataRefreshStatus: RefreshStatus) => {
  if (dataRefreshStatus === RefreshStatus.IN_PROGRESS) {
    message.info(
      'Your account data refresh is in progress. Please try again in some time'
    );
    return false;
  }
  return true;
};

export const formatCategoryName = (categoryCode: string): string => {
  if (!categoryCode) return 'Others';

  const categoryMap: { [key: string]: string } = {
    SUBSCRIPTION: 'Subscriptions',
    UTILITIES: 'Bills & Utilities',
    EMI: 'EMI Payments',
    INSURANCE: 'Insurance',
    MUTUAL_FUND: 'SIP/Investment',
    RENT: 'Rent',
    LOAN: 'Loan Payments',
    OTHERS: 'Others',
  };

  if (categoryMap[categoryCode]) {
    return categoryMap[categoryCode];
  }

  return categoryCode
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const accountCashflowMonthly = (
  data: any,
  statusData: any,
  accountsListFromAnalyticsStatus: any,
  month: string,
  selectedAccount?: string
): Record<AccountCashflowCategoryType, number> => {
  const listAccountDetails = data.current.ACCOUNT_DETAILS;
  const currentMonthYear = month;
  const initSelectedAccounts: any[] = [];
  const categories = {
    credit: 0,
    debit: 0,
    investment: 0,
  };

  if (!selectedAccount) {
    listAccountDetails?.accountProfileList?.forEach((accountDetails: any) => {
      const accounts = accountDetails.account;
      const checkIfAccountExist = statusData.current.accountDetails.find(
        (preExistingAccData: any) =>
          preExistingAccData.linkRefNumber === accounts.linkedAccRef &&
          preExistingAccData.status === 'ACTIVE'
      );
      if (checkIfAccountExist) {
        initSelectedAccounts.push(accounts.linkedAccRef);
      }
    });
  } else initSelectedAccounts.push(selectedAccount);

  if (accountsListFromAnalyticsStatus.current) {
    Object.keys(accountsListFromAnalyticsStatus.current).forEach(
      (accRefNo: string) => {
        if (initSelectedAccounts.includes(accRefNo)) {
          const accountStats = accountsListFromAnalyticsStatus.current[
            accRefNo
          ].monthlyAnalysis.find((acc: any) => {
            return acc.yearMonth === currentMonthYear;
          });
          categories.debit += accountStats?.debitAmount || 0;
          categories.credit += accountStats?.creditAmount || 0;
          categories.investment += accountStats?.investment || 0;
        }
      }
    );
  }

  return {
    credit: categories.credit,
    debit: categories.debit,
    investment: categories.investment,
  };
};

export const allAccountsCashflowOverall = (
  accountsListFromAnalyticsStatus: any
): Record<AccountCashflowCategoryType, number> => {
  const categories = {
    credit: 0,
    debit: 0,
    investment: 0,
  };

  if (accountsListFromAnalyticsStatus.current) {
    Object.keys(accountsListFromAnalyticsStatus.current).forEach(
      (accRefNo: string) => {
        const accountStats = accountsListFromAnalyticsStatus.current[accRefNo];
        const monthlyAnalysis = accountStats?.monthlyAnalysis;
        monthlyAnalysis?.forEach((data: any) => {
          categories.debit += data?.debitAmount || 0;
          categories.credit += data?.creditAmount || 0;
          categories.investment += data?.investment || 0;
        });
      }
    );
  }

  return {
    credit: categories.credit,
    debit: categories.debit,
    investment: categories.investment,
  };
};

// Dedups DEPOSIT accountDetails by linkRefNumber, preferring an ACTIVE
// entry over a REVOKE_INITIATED one for the same account.
export const getRawDepositAccounts = (
  statusData: any,
  onlyDelivered: boolean = false
) => {
  const accountMap = new Map();

  statusData.current.accountDetails?.forEach((acc: any) => {
    if (
      acc.status !== 'INACTIVE' &&
      acc.fiType === 'DEPOSIT' &&
      (!onlyDelivered || acc.lastRawDataFetchedAt != null)
    ) {
      const existing = accountMap.get(acc.linkRefNumber);

      if (!existing) {
        accountMap.set(acc.linkRefNumber, acc);
      } else if (
        existing.status === 'REVOKE_INITIATED' &&
        acc.status === 'ACTIVE'
      ) {
        accountMap.set(acc.linkRefNumber, acc);
      }
    }
  });

  return Array.from(accountMap.values());
};

export const getActiveBankAccounts = (statusData: any) =>
  getRawDepositAccounts(statusData, true);

const structurizeAnalyticsAccounts = (accountProfile: any, rawData: any) => {
  const { account, holders, summary, dailyAnalytics } = accountProfile;  
  const holder = holders?.Holder?.[0];
  let balance;
  if (rawData?.[account.linkedAccRef]) {
    balance = rawData[account.linkedAccRef];
  } else balance = summary?.CurrentBalance ?? '';
  return {
    id: account.id,
    accNo: account.number.slice(-6),
    balance,
    lastUpdatedTime: '',
    ifscCode: summary?.ifsc ?? '',
    fipName: account.bank ?? '',
    accType: account.acctype ?? '',
    holderName: holder?.name ?? '',
    holderAddress: holder?.address ?? '',
    holderEmail: holder?.email ?? '',
    dailyAnalytics,
    fipId: account.fipId,
    date: account.lastRawDataFetchedAt,
    consentReferenceId: account.consentReferenceId,
    priority: account.priority,
    linkedAccRef: account.linkedAccRef,
  };
};

// Check which account's analytics process is completed out
// of accounts present in raw data
export const analyticsAccountFromRawAccount = (data: any, accounts: any[]) => {
const rawData = data.current.RAW_DATA;
  const depositAccountRefs = new Set<string>();
  const accountDetailsAnalytics =
    data.current.ACCOUNT_DETAILS?.accountProfileList ?? [];
  const accountsWithAnalyticsData: any[] = [];

  accounts.forEach((acc: any) => {
    depositAccountRefs.add(acc.linkRefNumber);
  });  
  for (let accProfile of accountDetailsAnalytics) {
    if (depositAccountRefs.has(accProfile.account.linkedAccRef)) {      
      accountsWithAnalyticsData.push(
        structurizeAnalyticsAccounts(accProfile, rawData)
      );
    }
  }

  return accountsWithAnalyticsData;
};

// For bar graph data, format api response to new bar chart data form
export const formatMonthlyTransactionData = (input: any) => {
  return {
    month: `${getMonthAbbreviation(input.month)} ${String(input.year).slice(-2)}`,
    DEBIT: input.totalDebitAmount,
    CREDIT: input.totalCreditAmount,
    INVESTMENT: input.totalInvestmentExpense,
    key: `${input.year}-${String(input.month).padStart(2, '0')}`,
  };
};

const hasTransactionValue = (
  item: any,
  type: 'DEBIT' | 'CREDIT' | 'INVESTMENT' | null,
  source: 'formatted' | 'api'
) => {
  if (source === 'formatted') {
    switch (type) {
      case 'DEBIT':
        return item.DEBIT > 0;

      case 'CREDIT':
        return item.CREDIT > 0;

      case 'INVESTMENT':
        return item.INVESTMENT > 0;

      default:
        return false;
    }
  }

  switch (type) {
    case 'DEBIT':
      return item.totalDebitAmount > 0;

    case 'CREDIT':
      return item.totalCreditAmount > 0;

    case 'INVESTMENT':
      return item.totalInvestmentExpense > 0;

    default:
      return false;
  }
};

export const handleExistingTrendsData = ({
  trends,
  trendKey,
  graphSelectedMonth,
  categoryType,
}: handleExistingTrendsDataType) => {
  const trendData = trends[trendKey];

  const currentMonth = trendData.find(
    (item: any) => item.key === graphSelectedMonth
  );

  if (
    currentMonth &&
    hasTransactionValue(currentMonth, categoryType, 'formatted')
  ) {
    return currentMonth.key;
  }

  for (let i = trendData.length - 1; i >= 0; i--) {
    if (hasTransactionValue(trendData[i], categoryType, 'formatted')) {
      return trendData[i].key;
    }
  }

  return null;
};

export const processTrendResponse = (
  res: any[],
  categoryType: categoryTrendsType
) => {
  if (res.length === 0) {
    return {
      formattedTrends: [],
      selectedMonth: null,
      averages: {
        credit: 0,
        debit: 0,
        investment: 0,
      },
    };
  }

  let avgCredit = 0;
  let avgDebit = 0;
  let avgInvestment = 0;

  let selectedMonth: string | null = null;

  const formattedTrends = res.map((input: any) => {
    if (!selectedMonth && hasTransactionValue(input, categoryType, 'api')) {
      selectedMonth = `${input.year}-${String(input.month).padStart(2, '0')}`;
    }

    avgDebit += input.totalDebitAmount;
    avgCredit += input.totalCreditAmount;
    avgInvestment += input.totalInvestmentExpense;

    return formatMonthlyTransactionData(input);
  });

  return {
    formattedTrends: formattedTrends.reverse(),
    selectedMonth,
    averages: {
      credit: avgCredit / res.length,
      debit: avgDebit / res.length,
      investment: avgInvestment / res.length,
    },
  };
};

export const buildTransactionCategoryMap = (
  data: any[],
  categoryType: string
) => {
  const categoryCode: Record<string, string[]> = {};
  const categoryName: Record<string, string[]> = {};

  const excludedCategories =
    categoryType === 'DEBIT'
      ? DEBIT_EXCLUDED_CATEGORIES
      : categoryType === 'CREDIT'
        ? CREDIT_EXCLUDED_CATEGORIES
        : [];

  data.forEach((category) => {
    let filteredCodes = category.categoryCodes;
    let filteredNames = category.categoryName;

    if (excludedCategories.length) {
      filteredCodes = [];
      filteredNames = [];

      category.categoryCodes.forEach((code: string, index: number) => {
        if (!excludedCategories.includes(code)) {
          filteredCodes.push(code);
          filteredNames.push(category.categoryName[index]);
        }
      });
    }

    const key = `${category.accountId}_${categoryType}`;

    categoryCode[key] = filteredCodes;
    categoryName[key] = filteredNames;
  });

  return {
    categoryCode,
    categoryName,
  };
};

// Initials for a bank/institution name, e.g. "Bank of Baroda" -> "BOB".
export const getBankInitials = (bankName?: string): string =>
  (bankName ?? '')
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase();

// Normalizes accountProfileList entries into the {id, accNo, fipName, fipId,
// linkedAccRef} shape used for account chips/selectors, optionally scoped to
// a single accountId.
export const mapAccountProfilesToBankAccounts = (
  accountProfileList: any[] | undefined,
  filterAccountId?: string
) => {
  const profiles = accountProfileList ?? [];
  const scopedProfiles = filterAccountId
    ? profiles.filter((profile: any) => profile.account.id === filterAccountId)
    : profiles;

  return scopedProfiles.map((profile: any) => {
    const account = profile.account;
    return {
      id: account.id,
      accNo: account.number.slice(-6),
      fipName: account.bank,
      fipId: account.fipId,
      linkedAccRef: account.linkedAccRef,
    };
  });
};

export const findMonthlyStatsByMonthYear = (
  monthlyList: any[] | undefined,
  monthYear: string
) => monthlyList?.find((item: any) => item.monthYear === monthYear);

// Builds Radar/chart-ready category data ({name, amount, percentage}) for a
// single month from a TOP5FUNDTRANSFER `monthly` list.
export const getCategoryChartDataForMonth = (
  monthlyList: any[] | undefined,
  monthYear: string
) => {
  const monthlyTxns = findMonthlyStatsByMonthYear(monthlyList, monthYear)?.txns;
  const totalAmount = monthlyTxns?.reduce(
    (sum: number, txn: any) => sum + txn.amount,
    0
  );
  return monthlyTxns?.map((txn: any) => ({
    name: txn.category,
    amount: txn.amount,
    percentage: ((txn.amount * 100) / totalAmount).toFixed(2),
  }));
};

export const processCategoryBreakdown = (transactions: any[]) => {
  const categories: {
    [key: string]: { count: number; totalAmount: number; transactions: any[] };
  } = {};

  transactions.forEach((txn: any) => {
    const category = txn.categoryCode || txn.category || 'OTHERS';
    if (!categories[category]) {
      categories[category] = { count: 0, totalAmount: 0, transactions: [] };
    }
    categories[category].count += 1;
    categories[category].totalAmount += txn.lastAmount || 0;
    categories[category].transactions.push(txn);
  });

  return Object.entries(categories).map(([category, data]) => ({
    category: formatCategoryName(category),
    originalCategory: category,
    count: data.count,
    totalAmount: data.totalAmount,
    transactions: data.transactions,
  }));
};

export type MonthlyTransactionDateGroup = {
  date: string;
  label: string;
  items: any[];
};

export const groupTransactionsByDate = (
  transactions: any[]
): MonthlyTransactionDateGroup[] => {
  const map: Record<string, MonthlyTransactionDateGroup> = {};
  transactions.forEach((txn) => {
    const txnDate = dayjs(txn.lastTransactionDate);
    const key = txnDate.format('YYYY-MM-DD');
    if (!map[key]) {
      map[key] = { date: key, label: txnDate.format('D MMM YYYY'), items: [] };
    }
    map[key].items.push(txn);
  });
  return Object.values(map).sort(
    (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
  );
};

// Splits active recurring transactions into completed/pending for the
// current month and totals each bucket, plus a category breakdown across
// all of them.
export const getMonthlyPaymentsSummary = (monthlyRecurringData: any[]) => {
  const filteredData = (monthlyRecurringData ?? [])
    .filter((txn: any) => txn.active)
    .sort(
      (a: any, b: any) =>
        dayjs(a.lastTransactionDate).valueOf() -
        dayjs(b.lastTransactionDate).valueOf()
    );

  const today = dayjs();
  const currentDate = today.date();
  const currentMonth = today.month() + 1;

  const { completedTxn, pendingTxn, totalPaidAmount, totalPendingAmount } =
    filteredData.reduce(
      (acc: any, txn: any) => {
        const txnDate = dayjs(txn.lastTransactionDate);
        const isCompleted =
          currentMonth === txnDate.month() + 1 && currentDate > txnDate.date();

        if (isCompleted) {
          acc.completedTxn.push(txn);
          acc.totalPaidAmount += txn.lastAmount;
        } else {
          acc.pendingTxn.push(txn);
          acc.totalPendingAmount += txn.lastAmount;
        }

        return acc;
      },
      {
        completedTxn: [] as any[],
        pendingTxn: [] as any[],
        totalPaidAmount: 0,
        totalPendingAmount: 0,
      }
    );

  return {
    completedTxn,
    pendingTxn,
    categoryBreakdown: processCategoryBreakdown(filteredData),
    totalPaidAmount,
    totalPendingAmount,
  };
};

// Caps a {group: transactions[]} map to at most `maxCount` items total,
// dropping later groups once the cap is hit. No-op when maxCount is unset.
export const limitGroupedTransactions = (
  transactions: Record<string, any[]>,
  maxCount?: number
): Record<string, any[]> => {
  if (!maxCount) return transactions;
  let count = 0;
  const result: Record<string, any[]> = {};
  for (const [group, transactionList] of Object.entries(transactions)) {
    if (count >= maxCount) break;
    const remaining = maxCount - count;
    const items = transactionList.slice(0, remaining);
    if (items.length) {
      result[group] = items;
      count += items.length;
    }
  }
  return result;
};

// A transaction's merchant name lives in whichever of these fields is
// populated, formatted as "subtitle-title" (e.g. "Food-Swiggy").
export const getTransactionTitleAndSubtitle = (
  txnData: any
): { title: string; subtitle: string } => {
  const merchantParts =
    txnData.displayName?.split('-') ??
    txnData.category?.split('-') ??
    txnData.narration?.split('-') ??
    [];

  const title = merchantParts.length > 1 ? merchantParts[1] : merchantParts[0];
  const subtitle = merchantParts.length > 1 ? merchantParts[0] : 'Others';

  return { title, subtitle };
};

export const getTransactionDirection = (
  type?: string
): { isCredit: boolean; isDebit: boolean } => {
  const txnType = type?.toLowerCase();
  return {
    isCredit: !!txnType?.startsWith('c'),
    isDebit: !!txnType?.startsWith('d'),
  };
};

export const getTransactionDisplayAmount = (
  txnData: any,
  isAmountVisible: boolean
): string => {
  if (txnData.format) return formatToINR(txnData.lastAmount, isAmountVisible);
  if (txnData.amount)
    return formatToINR(txnData.amount.toString(), isAmountVisible);
  return '';
};

// selectedAccountId.length === 1 scopes to that account's categories;
// multi-select (or "all accounts") dedups categories across the selection.
export const getCategoryOptionsForSelectedAccounts = (
  categoriesByAccount: Record<string, string[]>,
  selectedAccountId: string[]
): string[] =>
  selectedAccountId.length === 1
    ? (categoriesByAccount[selectedAccountId[0]] ?? [])
    : Array.from(
        new Set(
          selectedAccountId.flatMap(
            (accountId) => categoriesByAccount[accountId] ?? []
          )
        )
      );

export const buildCategoryCodesByAccountId = (
  categories: { accountId: string; categoryCodes: string[] }[]
): Record<string, string[]> => {
  const categoryCode: Record<string, string[]> = {};
  categories.forEach((category) => {
    categoryCode[category.accountId] = category.categoryCodes;
  });
  return categoryCode;
};

// Dedups month keys across multiple accounts' month-filter responses into
// {value, label} Select options, sorted newest-first (month keys are
// "YYYY-MM" — the API doesn't guarantee any particular order across accounts).
export const buildUniqueMonthOptions = (
  monthLists: string[][]
): { value: string; label: string }[] => {
  const uniqueMonths = Array.from(new Set(monthLists.flat()));
  const sortedMonths = uniqueMonths.sort((a, b) => {
    const [yearA, monthA] = a.split('-').map(Number);
    const [yearB, monthB] = b.split('-').map(Number);
    return yearB !== yearA ? yearB - yearA : monthB - monthA;
  });
  return sortedMonths.map((month) => ({
    value: month,
    label: convertToMonthYear(month),
  }));
};

// e.g. 0.4% stays "0.40", but 12.3% rounds to "12" — sub-1% categories keep
// two decimals so they don't all collapse to "0%".
export const getCategoryPercentageLabel = (
  txnAmount: number,
  totalAmount: number
): string => {
  const percentage = (txnAmount / totalAmount) * 100;
  return Number(percentage.toFixed(2)) < 1
    ? percentage.toFixed(2)
    : percentage.toFixed(0);
};
